import { Line } from 'react-chartjs-2'

import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

/**
 * 積み上げ面グラフの系列色。**8色で固定し、順番に割り当てる**（巡回させない）。
 *
 * 色は識別のためだけにあるので、色覚特性で見分けられない組み合わせを使わない。
 * この8色は隣接ペアのCVD分離・明度帯・彩度・面surfaceとのコントラストを検証済み
 * （コントラストは3:1を下回る色があるため、**凡例と件数の併記が必須**。
 * 色だけで系列を識別させない）。
 */
export const STACKED_AREA_COLORS = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
  '#4a3aa7',
  '#e34948'
]

/** 上限を超えた系列をまとめる「その他」。系列色とは別枠の灰にする */
export const STACKED_AREA_OTHERS_COLOR = '#9AA3AE'

const buildOptions = (unit: string): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  // 縦のどこを指しても同じ日の全系列が出る（1点ずつ狙わせない）
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      align: 'end',
      labels: {
        color: '#34414F',
        font: { size: 11 },
        boxWidth: 10,
        boxHeight: 10,
        padding: 8,
        usePointStyle: true,
        pointStyle: 'rectRounded'
      }
    },
    tooltip: {
      backgroundColor: 'rgba(2, 6, 23, 0.95)',
      titleFont: { size: 11, weight: 'bold' },
      bodyFont: { size: 11 },
      padding: 8,
      cornerRadius: 4,
      displayColors: true,
      callbacks: {
        // 欠測点では parsed.y が null になる。0 として出す
        label: (ctx) => `${ctx.dataset.label}: ${(ctx.parsed.y ?? 0).toLocaleString()}${unit}`
      }
    }
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      border: { display: false },
      ticks: { font: { size: 10 }, color: '#34414F', maxRotation: 0, autoSkipPadding: 24 }
    },
    y: {
      stacked: true,
      beginAtZero: true,
      border: { display: false },
      grid: { color: '#E4E8EE' },
      ticks: {
        font: { size: 10 },
        color: '#34414F',
        padding: 6,
        count: 6,
        callback: (value) => `${value}${unit}`
      }
    }
  },
  elements: {
    // 帯の境目に面と同じ色の細い線を置くと、隣接する帯が地続きに見える。
    // 背景色の細い縁で 2px 分空ける
    line: { borderWidth: 2, borderColor: '#FFFFFF', tension: 0 },
    point: { radius: 0, hoverRadius: 4, hitRadius: 8 }
  }
})

interface Props {
  labels: string[]
  items: {
    title: string
    values: number[]
    /** 省略時は STACKED_AREA_COLORS から順に割り当てる */
    color?: string
  }[]
  unit?: string
}

const StackedAreaChart = ({ labels, items, unit = '' }: Props) => (
  <Line
    data={{
      labels,
      datasets: items.map((item, i) => {
        const color = item.color ?? STACKED_AREA_COLORS[i % STACKED_AREA_COLORS.length]
        return {
          label: item.title,
          data: item.values,
          backgroundColor: color,
          borderColor: '#FFFFFF',
          fill: true
        }
      })
    }}
    options={buildOptions(unit)}
  />
)

export default StackedAreaChart
